import { ReadFile } from '@paragon/client-tools';
import { HangerPrice } from '@paragon/contract-library';
import { designService, NewHangerPrice } from '@paragon/data-connections';
import { enqueueError } from '@paragon/notification-tools';
import { addSpinnerMessage, removeSpinnerMessage } from '@paragon/task-spinner';

import { setHangerPrices } from '../store/hangerPricesSlice';
import { AsyncThunkActionBoolean, AsyncThunkActionVoid } from './thunkActions';

export const getAllHangerPrices = (): AsyncThunkActionVoid => async (dispatch) => {
  const spinnerId = dispatch(addSpinnerMessage('Loading Hanger Prices'));

  try {
    dispatch(setHangerPrices(await designService.hangerPrices.getAllHangerPrices()));
  } finally {
    dispatch(removeSpinnerMessage(spinnerId));
  }
};

export const importHangerPrices =
  (file: ReadFile): AsyncThunkActionVoid =>
  async (dispatch) => {
    const spinnerId = dispatch(addSpinnerMessage('Importing Hanger Prices'));
    try {
      const hangerPriceRecords = await designService.hangerPrices.importHangerPrices(file);
      dispatch(setHangerPrices(hangerPriceRecords));
    } catch (exception) {
      if ('message' in (exception as Error)) {
        dispatch(enqueueError((exception as Error).message));
      } else {
        dispatch(enqueueError('Error importing'));
      }
    } finally {
      dispatch(removeSpinnerMessage(spinnerId));
    }
  };

export const insertHangerPrice =
  (newHangerPrice: NewHangerPrice): AsyncThunkActionBoolean =>
  async (dispatch) => {
    const spinnerId = dispatch(addSpinnerMessage('Adding Hanger Price'));

    try {
      const addedHangerPrice = await designService.hangerPrices.postHangerPrice(newHangerPrice);
      dispatch(setHangerPrices([addedHangerPrice]));
      return true;
    } catch (error) {
      console.error(error);
      dispatch(enqueueError('Error inserting hanger price'));
      return false;
    } finally {
      dispatch(removeSpinnerMessage(spinnerId));
    }
  };

export const editHangerPrices =
  (hangerPriceRecords: readonly HangerPrice[]): AsyncThunkActionBoolean =>
  async (dispatch) => {
    const spinnerId = dispatch(
      addSpinnerMessage(
        hangerPriceRecords.length === 1 ? 'Editing Price' : `Editing ${hangerPriceRecords.length} Hanger Prices`,
      ),
    );

    try {
      const edits = hangerPriceRecords.map(({ guid, price, notes }) => ({ guid, price, notes }));
      const newPriceRecords = await designService.hangerPrices.putHangerPrices(edits);
      dispatch(setHangerPrices(newPriceRecords));
      return true;
    } catch {
      dispatch(
        enqueueError(
          hangerPriceRecords.length === 1
            ? 'Error editing hanger price.'
            : `Error editing ${hangerPriceRecords.length} hanger prices.`,
        ),
      );
      return false;
    } finally {
      dispatch(removeSpinnerMessage(spinnerId));
    }
  };
